import { useEffect, useRef, useState } from 'react';
import { adviserFactsheetOf } from '../../../utils/client-factsheet';
import type { QueueClient } from '../../../types/client';
import type {
  AdviserNotesPatch,
  QuizCompletedAt,
  QuizPendingUntil,
  RecruitmentNotes,
  SmartIdeaNotes
} from '../../../store/clientNotes/api';
import { clientNotesTransport } from './clientNotesTransport';

/**
 * The adviser's notes on each client — 招募 stage, scores, chips, path,
 * 他是誰, Smart IDEA marks — as the server holds them on the factsheet
 * (docs/specs/2026-09-24-demo-localstorage-to-db-design.md §3, §7, §17).
 *
 * Seeded from the client objects the page already loads, so reading costs no
 * request; written through PATCH adviser-notes, optimistically. The modules that
 * used to keep these in localStorage (recruitStage, recruitScore, …) keep their
 * read / write / use functions and read and write here instead.
 *
 * A client with a write in flight is not re-seeded: a list refresh that left
 * the server before the write would otherwise put the old value back.
 */

export type ClientNotes = {
  /** Null for a client with no factsheet — nothing can be saved for them (§5.1). */
  factsheetId: string | null;
  recruitment: RecruitmentNotes;
  smartIdea: SmartIdeaNotes;
  quizCompletedAt: QuizCompletedAt;
  /** Until when an unanswered part-1 link of each quiz is live. */
  quizPendingUntil: QuizPendingUntil;
  /** 現職 — the root factsheet field 他是誰 reuses (§13 R1). */
  occupation: string;
  /** 介紹人 — root `referrerName`. */
  referrerName: string;
};

export const CLIENT_NOTES_CHANGED_EVENT = 'clientv2-notes-changed';
export const CLIENT_NOTES_SAVE_FAILED_EVENT = 'clientv2-notes-save-failed';

const entries = new Map<string, ClientNotes>();
const pending = new Map<string, number>();
type RootKey = 'occupation' | 'referrerName';
type Confirmed = Pick<ClientNotes, 'recruitment' | 'smartIdea'> & Record<RootKey, string>;
/** The server's copy as last seen (seed or save answer) — what a failed write goes back to. */
const confirmed = new Map<string, Confirmed>();
/** 現職 / 介紹人 written but not yet seen back from the server. */
const rootEdits = new Map<string, Partial<Record<RootKey, string>>>();
/** Every write gets the next number; `confirmed` only takes a reply from a write
 * sent later than the one it holds, so replies arriving out of order cannot put
 * an older server copy over a newer one. */
let sentSeq = 0;
const confirmedSeq = new Map<string, number>();

const announce = (clientId: string | null) =>
  window.dispatchEvent(new CustomEvent(CLIENT_NOTES_CHANGED_EVENT, { detail: { clientId } }));

const factsheetIdOf = (factsheet: unknown): string | null => {
  if (typeof factsheet === 'string') return factsheet || null;
  const id = factsheet && (factsheet as { _id?: unknown })._id;
  return typeof id === 'string' && id ? id : null;
};

export const notesFromClient = (client: QueueClient, adviserEmail?: string): ClientNotes => {
  const factsheet = adviserFactsheetOf(client, adviserEmail) as unknown;
  const doc = factsheet && typeof factsheet === 'object' ? (factsheet as Record<string, any>) : {};
  return {
    factsheetId: factsheetIdOf(factsheet),
    recruitment: doc.recruitment || {},
    smartIdea: doc.smartIdea || {},
    quizCompletedAt: doc.quizCompletedAt || {},
    quizPendingUntil: doc.quizPendingUntil || {},
    occupation: typeof doc.occupation === 'string' ? doc.occupation : '',
    referrerName: typeof doc.referrerName === 'string' ? doc.referrerName : ''
  };
};

/** Server copies in. Returns whether anything changed; announces it unless `notify: false`. */
export const seedClientNotes = (
  clients: QueueClient[] | null | undefined,
  options: { adviserEmail?: string; notify?: boolean } = {}
): boolean => {
  let changed = false;
  (clients || []).forEach(client => {
    if (!client || !client._id || pending.get(client._id)) return;
    const next = notesFromClient(client, options.adviserEmail);
    confirmedSeq.set(client._id, sentSeq);
    confirmed.set(client._id, {
      recruitment: next.recruitment,
      smartIdea: next.smartIdea,
      occupation: next.occupation,
      referrerName: next.referrerName
    });
    // A 現職 / 介紹人 edit outlives a seed that predates it: its own re-read
    // (after the factsheet update) brings it back, and only then is it dropped.
    const edits = rootEdits.get(client._id);
    if (edits) {
      (Object.keys(edits) as RootKey[]).forEach(key => {
        if (next[key] === edits[key]) delete edits[key];
        else next[key] = edits[key] as string;
      });
      if (!Object.keys(edits).length) rootEdits.delete(client._id);
    }
    const previous = entries.get(client._id);
    if (previous && JSON.stringify(previous) === JSON.stringify(next)) return;
    entries.set(client._id, next);
    changed = true;
  });
  if (changed && options.notify !== false) announce(null);
  return changed;
};

/**
 * Seeds during render — so the first paint already reads the server copy — and
 * announces after commit, since announcing during render would update other
 * components while this one renders.
 */
export const useSeedClientNotes = (clients: QueueClient[] | null | undefined, adviserEmail?: string) => {
  const seen = useRef<unknown>(null);
  const toAnnounce = useRef(false);
  if (clients && seen.current !== clients) {
    seen.current = clients;
    if (seedClientNotes(clients, { adviserEmail, notify: false })) toAnnounce.current = true;
  }
  useEffect(() => {
    if (!toAnnounce.current) return;
    toAnnounce.current = false;
    announce(null);
  });
};

export const readClientNotes = (clientId?: string | null): ClientNotes | null =>
  (clientId && entries.get(clientId)) || null;

/** One client's notes, re-rendering whenever they change. */
export const useClientNotes = (clientId?: string | null): ClientNotes | null => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ clientId: string | null }>).detail;
      if (!detail || detail.clientId === null || detail.clientId === clientId) setVersion(v => v + 1);
    };
    window.addEventListener(CLIENT_NOTES_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CLIENT_NOTES_CHANGED_EVENT, onChange);
  }, [clientId]);
  return readClientNotes(clientId);
};

/** Whether this client's notes can be saved (it has a factsheet). Re-renders only
 * when that answer changes, not on every notes edit — the list has a row each. */
export const useCanEditClientNotes = (clientId?: string | null): boolean => {
  const canEditOf = () => Boolean(readClientNotes(clientId)?.factsheetId);
  const [canEdit, setCanEdit] = useState(canEditOf);
  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ clientId: string | null }>).detail;
      if (!detail || detail.clientId === null || detail.clientId === clientId) setCanEdit(canEditOf());
    };
    setCanEdit(canEditOf());
    window.addEventListener(CLIENT_NOTES_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CLIENT_NOTES_CHANGED_EVENT, onChange);
    // canEditOf reads only `clientId`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);
  return canEdit;
};

/**
 * Re-reads this client from the server so the next seed brings its notes back
 * in sync — used after a change made outside `updateClientNotes` (a cleared
 * quiz result changes `quizCompletedAt`, which only a client re-read refreshes).
 * Reuses the transport's own re-read (`failed`, despite the name — see
 * `clientNotesTransport.ts`), so no new transport call needs mocking.
 */
export const reloadClientNotes = (clientId: string | null | undefined): void => {
  if (clientId) clientNotesTransport.failed(clientId);
};

const settle = (clientId: string): number => {
  const left = (pending.get(clientId) || 1) - 1;
  if (left > 0) pending.set(clientId, left);
  else pending.delete(clientId);
  return left;
};

/**
 * One adviser-notes write: `apply` is the optimistic local change, `patch` the
 * request body. The server's copy replaces the local one when the LAST write in
 * flight for this client settles. A failure puts back only the subdocument(s)
 * this write's `patch` touched (`recruitment` and/or `smartIdea`) to their
 * pre-write values, leaving anything else changed in the meantime alone, and
 * re-reads the client — whose next seed corrects anything else — and announces
 * the failure (the page shows a toast). Resolves false when nothing was saved.
 */
export const updateClientNotes = async (
  clientId: string | null | undefined,
  patch: AdviserNotesPatch,
  apply: (notes: ClientNotes) => ClientNotes
): Promise<boolean> => {
  const before = clientId ? entries.get(clientId) : undefined;
  if (!clientId || !before || !before.factsheetId) return false;
  const factsheetId = before.factsheetId;
  entries.set(clientId, apply(before));
  pending.set(clientId, (pending.get(clientId) || 0) + 1);
  sentSeq += 1;
  const seq = sentSeq;
  announce(clientId);
  try {
    const saved = await clientNotesTransport.patch(factsheetId, patch);
    const server = { recruitment: saved.recruitment || {}, smartIdea: saved.smartIdea || {} };
    // The reply of the most recently SENT write is the newest server copy,
    // whatever order the replies arrive in; an older one is not kept, and not
    // handed to Redux either (the next seed would bring it back).
    const newest = seq > (confirmedSeq.get(clientId) ?? -1);
    if (newest) {
      confirmedSeq.set(clientId, seq);
      confirmed.set(clientId, { ...(confirmed.get(clientId) || { occupation: '', referrerName: '' }), ...server });
    }
    if (settle(clientId) === 0) {
      const latest = entries.get(clientId) || before;
      const known = confirmed.get(clientId);
      entries.set(clientId, {
        ...latest,
        recruitment: known ? known.recruitment : server.recruitment,
        smartIdea: known ? known.smartIdea : server.smartIdea
      });
      announce(clientId);
    }
    if (newest) clientNotesTransport.saved(factsheetId, saved);
    return true;
  } catch (error) {
    // With other writes still in flight, the last to settle decides: a success
    // brings the server's copy (without this write), a failure lands here. The
    // last one goes back to the server's copy as last seen — never to this
    // write's own snapshot, which would wipe writes made after it, or bring
    // back an earlier failed one.
    if (settle(clientId) === 0) {
      const current = entries.get(clientId) || before;
      const known = confirmed.get(clientId);
      entries.set(clientId, {
        ...current,
        recruitment: known ? known.recruitment : before.recruitment,
        smartIdea: known ? known.smartIdea : before.smartIdea
      });
      announce(clientId);
    }
    clientNotesTransport.failed(clientId);
    window.dispatchEvent(new CustomEvent(CLIENT_NOTES_SAVE_FAILED_EVENT, { detail: { clientId } }));
    return false;
  }
};

/**
 * 現職 / 介紹人: mirrored here for 他是誰, written through the factsheet's own
 * update (its failures surface through the drawer's existing fact-write toast).
 */
export const setClientNotesRootField = (
  clientId: string | null | undefined,
  key: 'occupation' | 'referrerName',
  value: string
): boolean => {
  const current = clientId ? entries.get(clientId) : undefined;
  if (!clientId || !current || !current.factsheetId || current[key] === value) return false;
  entries.set(clientId, { ...current, [key]: value });
  rootEdits.set(clientId, { ...rootEdits.get(clientId), [key]: value });
  announce(clientId);
  clientNotesTransport.writeRootField(clientId, current.factsheetId, key, value);
  return true;
};

/**
 * A factsheet update failed (the drawer's fact-write failure count went up — it
 * cannot say which): every 現職 / 介紹人 not yet seen back goes back to the
 * server's copy.
 */
export const dropClientNotesRootEdits = (): void => {
  if (!rootEdits.size) return;
  rootEdits.forEach((edits, clientId) => {
    const current = entries.get(clientId);
    const known = confirmed.get(clientId);
    if (!current || !known) return;
    const restored = { ...current };
    (Object.keys(edits) as RootKey[]).forEach(key => {
      restored[key] = known[key];
    });
    entries.set(clientId, restored);
  });
  rootEdits.clear();
  announce(null);
};

/** For the transport's test double: the notes of the client on this factsheet. */
export const __readClientNotesByFactsheetForTests = (factsheetId: string): ClientNotes | null => {
  let found: ClientNotes | null = null;
  entries.forEach(notes => {
    if (notes.factsheetId === factsheetId) found = notes;
  });
  return found;
};

export const __resetClientNotesForTests = () => {
  entries.clear();
  pending.clear();
  confirmed.clear();
  confirmedSeq.clear();
  rootEdits.clear();
};
